import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaInternshipHit } from '#/modules/search/algolia/types/SearchAlgoliaInternshipHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import { type SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { distanceInWordsToNow } from '#/utils/date';

import { SearchInternshipHitInfo } from './SearchInternshipHitInfo';

type Props = {
  hit: SearchAlgoliaInternshipHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions?: ReactNode;
};

export function SearchInternshipHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
  application,
  actions,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const {
    name,
    published,
    city,
    state,
    country,
    orgName,
    orgType,
    logo,
    locationType,
    remoteCountry,
    remoteState,
    remoteZone,
    isPostedAnonymously,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: remoteCountry || country,
          remoteState: remoteState || state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteCountry, remoteState, remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: distanceInWordsToNow({
              date: fromUnixTime(published),
              locale,
              dtOverride: appConfig.idealist.dtOverride,
            }),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, locale, published, appConfig]);

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchInternshipHitInfo
            application={application}
            isPostedAnonymously={isPostedAnonymously}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
